import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Banknote, Check, CheckCircle2, Copy, CreditCard, FileText, Loader2, Send, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { copyPending, copyText } from '../lib/clipboard';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const fmtUzs = (v) => Math.round(Number(v || 0)).toLocaleString('ru-RU').replace(/ /g, ' ');
const fmtUsd = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const groupCard = (n) => n.replace(/(\d{4})(?=\d)/g, '$1 ');
const errorText = (err, fallback) => err?.response?.data?.detail || fallback;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('ru-RU') : '');

export default function Payouts() {
  const { t } = useTranslation('payouts');
  const MONTHS = t('common:months', { returnObjects: true });
  const now = new Date();
  const [params] = useSearchParams();
  // salaries are paid for the month that just ended (Reports links here with its own month)
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const urlMonth = Number(params.get('month'));
  const urlYear = Number(params.get('year'));
  const [year, setYear] = useState(String(Number.isInteger(urlYear) && urlYear >= 2000 && urlYear <= 2100 ? urlYear : prev.getFullYear()));
  const [month, setMonth] = useState(String(Number.isInteger(urlMonth) && urlMonth >= 1 && urlMonth <= 12 ? urlMonth : prev.getMonth() + 1));
  const [data, setData] = useState(null);
  const [revealed, setRevealed] = useState({}); // card id -> full number (copied once already)
  const [busy, setBusy] = useState(null); // employee id, or 'all' while sending every report
  const [showIncome, setShowIncome] = useState(false); // project income / expenses in the sent report (off, as in Reports before)

  // Only the latest request may set the list: an older month's late answer is dropped
  const latest = useRef(0);
  const load = useCallback(async () => {
    const seq = ++latest.current;
    try {
      const res = await axios.get(`${API_URL}/api/payouts?month=${month}&year=${year}`, { withCredentials: true });
      if (seq === latest.current) setData(res.data);
    } catch (err) {
      if (seq !== latest.current) return;
      toast.error(errorText(err, t('common:status.loadFailed')));
      setData({ items: [], totals: {} });
    }
  }, [month, year, t]);
  useEffect(() => { setData(null); setRevealed({}); load(); }, [load]);

  const copyCard = async (emp, card) => {
    let ok;
    if (revealed[card.id]) {
      ok = await copyText(revealed[card.id]);
    } else {
      const number = axios.post(`${API_URL}/api/payouts/reveal-card`, { employee_id: emp.employee_id, card_id: card.id },
        { withCredentials: true }).then((res) => res.data.card_number);
      ok = await copyPending(number);
      try {
        const n = await number;
        setRevealed((r) => ({ ...r, [card.id]: n }));
      } catch (err) {
        toast.error(errorText(err, t('toast.copyFailed')));
        load();
        return;
      }
    }
    if (ok) toast.success(t('toast.cardCopied', { last4: card.last4 }));
    else toast.error(t('toast.copyAgain'));
  };

  const copyAmount = async (emp) => {
    if (await copyText(String(emp.amount_uzs))) toast.success(t('toast.amountCopied', { amount: fmtUzs(emp.amount_uzs) }));
    else toast.error(t('toast.copyFailed'));
  };

  // the month the list was loaded for, not the selector (they differ while a new month loads)
  const period = () => `${MONTHS[data.month - 1]} ${data.year}`;

  const setPaid = async (emp, isPaid) => {
    const card = emp.cards[0];
    if (isPaid && !window.confirm(t('confirm.paid', { name: emp.full_name, amount: fmtUzs(emp.amount_uzs), period: period() }))) return;
    if (!isPaid && !window.confirm(t('confirm.unpaid', { name: emp.full_name, period: period() }))) return;
    setBusy(emp.employee_id);
    try {
      const res = await axios.post(`${API_URL}/api/payouts/mark-paid`, {
        employee_id: emp.employee_id, month: data.month, year: data.year, is_paid: isPaid,
        card_id: isPaid && emp.cards.length === 1 ? card.id : null,
        amount_uzs: isPaid && emp.amount_uzs > 0 ? emp.amount_uzs : null,
      }, { withCredentials: true });
      toast.success(isPaid && res.data.notified ? t('toast.paidNotified', { name: emp.full_name }) : res.data.message);
      await load();
    } catch (err) {
      toast.error(errorText(err, t('toast.markFailed')));
    } finally {
      setBusy(null);
    }
  };

  const sendReports = async (emps) => {
    const targets = emps.filter((e) => e.has_telegram);
    if (!targets.length) { toast.error(t('toast.noTelegram')); return; }
    const resend = targets.some((e) => e.report_sent_at);
    if (!window.confirm(t(resend ? 'confirm.resend' : 'confirm.send', { count: targets.length, period: period() }))) return;
    setBusy(emps.length === 1 ? emps[0].employee_id : 'all');
    try {
      const res = await axios.post(`${API_URL}/api/invoices/send`, {
        month: data.month, year: data.year, employee_ids: targets.map((e) => e.employee_id), show_income: showIncome,
      }, { withCredentials: true });
      if (res.data.success_count) toast.success(t('toast.reportsSent', { count: res.data.success_count }));
      (res.data.results || []).filter((r) => r.status !== 'sent')
        .forEach((r) => toast.error(`${r.employee_name}: ${r.reason || t('toast.sendFailed')}`));
      await load();
    } catch (err) {
      toast.error(errorText(err, t('toast.sendFailed')));
    } finally {
      setBusy(null);
    }
  };

  const downloadPdf = async (emp) => {
    try {
      const res = await axios.get(`${API_URL}/api/invoices/pdf/${emp.employee_id}?month=${data.month}&year=${data.year}&show_income=${showIncome}`,
        { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${emp.full_name}_${data.month}_${data.year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errorText(err, t('toast.pdfFailed')));
    }
  };

  // the usual three years, plus one opened from a link (e.g. Reports for an older year)
  const years = [...new Set([...Array.from({ length: 3 }, (_, i) => String(now.getFullYear() - 1 + i)), year])].sort();
  const items = data?.items || [];
  const totals = data?.totals || {};
  const canPay = !!data?.can_pay;
  const unsent = items.filter((e) => e.has_telegram && !e.report_sent_at);

  return (
    <div className="space-y-5" data-testid="payouts-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Banknote className="h-7 w-7 text-emerald-600" /> {t('title')}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{t(canPay ? 'subtitle' : 'subtitleStaff')}</p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex gap-3">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="flex-1 sm:w-44 sm:flex-none"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 sm:ml-2" title={t('showIncomeHint')}>
          <Switch checked={showIncome} onCheckedChange={setShowIncome} data-testid="show-income" />
          {t('showIncome')}
        </label>
        {data && unsent.length > 0 && (
          <Button variant="outline" className="gap-2 sm:ml-auto" disabled={!!busy} onClick={() => sendReports(unsent)} data-testid="send-all">
            {busy === 'all' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {t('sendAll', { count: unsent.length })}
          </Button>
        )}
      </div>

      {data && totals.count > 0 && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs uppercase text-slate-500">{t('due')}</p>
            <p className="text-xl font-bold mt-1">{fmtUzs(totals.due_uzs)} <span className="text-sm font-normal text-slate-500">UZS</span></p>
          </div>
          <div className="text-sm text-slate-500 text-right">
            <p>{t('paidCount', { paid: totals.paid_count, total: totals.count })}{totals.paid_uzs ? ` · ${fmtUzs(totals.paid_uzs)} UZS` : ''}</p>
            <p>{t('sentCount', { sent: totals.sent_count, total: totals.count })}</p>
          </div>
        </div>
      )}

      {!data && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
      {data && items.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">{t('empty')}</p>}

      <div className="grid gap-3 lg:grid-cols-2">
        {items.map((emp) => (
          <div key={emp.employee_id} data-testid={`payout-${emp.employee_id}`}
            className={`rounded-xl border p-4 space-y-3 ${emp.is_paid
              ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-900/10'
              : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800'}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-slate-900 dark:text-slate-100 truncate">{emp.full_name}</p>
                {emp.position && <p className="text-xs text-slate-500 truncate">{emp.position}</p>}
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0 text-xs font-medium">
                <span className={`inline-flex items-center gap-1 ${emp.report_sent_at ? 'text-blue-700 dark:text-blue-400' : 'text-slate-400'}`}
                  data-testid={`sent-${emp.employee_id}`}>
                  <Send className="h-3.5 w-3.5" /> {emp.report_sent_at ? t('reportSent', { date: fmtDate(emp.report_sent_at) }) : t('reportNotSent')}
                </span>
                {emp.is_paid && (
                  <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400" data-testid={`paid-${emp.employee_id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5" /> {t('paidOn', { date: fmtDate(emp.paid_at) })}
                  </span>
                )}
                {emp.status === 'processing' && (
                  <span className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('processing')}</span>
                )}
                {emp.status === 'failed' && (
                  <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400" title={emp.failure_reason || ''}>
                    <AlertTriangle className="h-3.5 w-3.5" /> {t('failed')}
                  </span>
                )}
              </div>
            </div>

            <button type="button" onClick={() => copyAmount(emp)} disabled={!emp.amount_uzs}
              className="w-full flex items-center justify-between rounded-lg bg-slate-50 dark:bg-slate-900 px-3 py-2.5 text-left active:bg-slate-100 dark:active:bg-slate-700">
              <span>
                <span className="block text-lg font-bold text-slate-900 dark:text-slate-100">{fmtUzs(emp.amount_uzs)} UZS</span>
                <span className="block text-xs text-slate-500">{fmtUsd(emp.net_salary)}</span>
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400"><Copy className="h-4 w-4" /> {t('copyAmount')}</span>
            </button>

            {emp.is_paid && emp.paid_amount_uzs != null && (
              <p className={`text-xs ${emp.amount_changed ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-700 dark:text-emerald-400'}`}>
                {t(emp.amount_changed ? 'paidDiffers' : 'paidAmount', { amount: fmtUzs(emp.paid_amount_uzs) })}
                {emp.paid_card_last4 ? ` → •••• ${emp.paid_card_last4}` : ''}
              </p>
            )}

            {!emp.has_card ? (
              <p className="text-xs text-amber-600 dark:text-amber-400">{t('noCard')}</p>
            ) : !canPay ? (
              <p className="text-xs text-slate-500 flex items-center gap-1"><CreditCard className="h-3.5 w-3.5" /> {t('hasCard')}</p>
            ) : emp.cards.map((card) => (
              <button type="button" key={card.id} onClick={() => copyCard(emp, card)} disabled={!canPay}
                className="w-full flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2.5 text-left active:bg-slate-50 dark:active:bg-slate-700 disabled:cursor-default">
                <span className="min-w-0">
                  <span className="flex items-center gap-2 font-mono text-sm text-slate-800 dark:text-slate-200">
                    <CreditCard className="h-4 w-4 text-slate-400 shrink-0" />
                    {revealed[card.id] ? groupCard(revealed[card.id]) : card.masked}
                  </span>
                  <span className="block text-xs text-slate-500 truncate">
                    {[card.card_type !== 'other' ? card.card_type.toUpperCase() : null, card.holder_name, card.label].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {canPay && <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 shrink-0"><Copy className="h-4 w-4" /> {t('copyCard')}</span>}
              </button>
            ))}

            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button variant="ghost" size="sm" className="gap-1" onClick={() => downloadPdf(emp)} title={t('pdfHint')}>
                <FileText className="h-4 w-4" /> PDF
              </Button>
              <Button variant="outline" size="sm" className="gap-1" disabled={!emp.has_telegram || !!busy}
                title={emp.has_telegram ? '' : t('noTelegram')} onClick={() => sendReports([emp])} data-testid={`send-${emp.employee_id}`}>
                {busy === emp.employee_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {t(emp.report_sent_at ? 'resend' : 'send')}
              </Button>
              {canPay && emp.status !== 'processing' && (emp.is_paid ? (
                <Button variant="ghost" size="sm" className="gap-1 text-slate-500" disabled={!!busy}
                  onClick={() => setPaid(emp, false)}>
                  <Undo2 className="h-4 w-4" /> {t('unmark')}
                </Button>
              ) : (
                <Button className="w-full sm:w-auto gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={!!busy}
                  onClick={() => setPaid(emp, true)} data-testid={`pay-${emp.employee_id}`}>
                  {busy === emp.employee_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {t('markPaid')}
                </Button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {data && items.length > 0 && <p className="text-xs text-slate-500">{t(canPay ? 'hint' : 'hintStaff')}</p>}
    </div>
  );
}
